import { Badge } from '@civilgrid/ui/Badge';
import { Label } from '@civilgrid/ui/Label';
import { Table } from '@civilgrid/ui/Table';
import { useParams } from '@tanstack/react-router';
import { useId } from 'react';
import { useGetProject } from '~/hooks/useGetProject';
import { useProjectIntelligence } from '~/hooks/useProjectIntelligence';
import type { ProjectConflict, ProjectConflictAgency } from '../../../../../../common/types';
import { getFormattedConflictCount } from '../../../common';
import { type ConflictTableRow, conflictColumns, groupConflictsByAgency, useSelectedConflictRowIds } from './common';

export function PavingMoratoria() {
  const headingId = useId();
  const { projectId } = useParams({ strict: false });

  const { data: project } = useGetProject({ projectId });
  const { data: projectIntelligence } = useProjectIntelligence({ projectId });

  const data: ConflictTableRow[] = groupConflictsByAgency(
    (projectIntelligence?.conflictAgencies ?? []) as ProjectConflictAgency[],
    (projectIntelligence?.projectConflicts ?? []) as ProjectConflict[],
    'pavingMoratorium',
    projectIntelligence?.intelligenceSummary?.conflictWindow,
    projectIntelligence?.intelligenceSummary?.conflictFeatures,
  );
  const selectedRowIds = useSelectedConflictRowIds(data);

  const conflictCount = projectIntelligence?.intelligenceSummary?.conflictWindow?.counts?.moratoria || 0;

  return (
    <div className="pb-3" id="paving-moratoria">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-1">
          <Label id={headingId} className="text-sm font-medium">
            Paving Moratoria
          </Label>
        </div>
        <Badge color={conflictCount === 0 ? 'green' : 'red'}>{getFormattedConflictCount(conflictCount)}</Badge>
      </div>
      <div>
        {data.length === 0 ? (
          <div className="text-center text-xs text-gray-500 py-2">No paving moratoria conflicts found.</div>
        ) : (
          <Table
            key={data[0]?.id}
            aria-labelledby={headingId}
            columns={conflictColumns}
            maxHeight={380}
            data={data}
            meta={{ projectId, organizationId: project?.organization }}
            enableExpanding={true}
            initialState={{ expanded: data[0] ? { [data[0].id]: true } : {} }}
            stickyExpandedRows={true}
            enableColumnFilters={false}
            enableColumnOrdering={false}
            enableColumnPinning={false}
            enableColumnGrouping={false}
            enableHiding={false}
            getRowId={(row: ConflictTableRow) => row.id}
            highlightedRowIds={selectedRowIds}
            getSubRows={(row: ConflictTableRow) => (row.rowType === 'agency' ? row.subRows : undefined)}
          />
        )}
      </div>
    </div>
  );
}
